import { intlLocale } from 'kanban/constants';

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Breaks a duration in seconds into the two most significant units so the UI can
 * render compact strings like "2d 4h" or "15m". Returns an empty array for nullish
 * input so callers can show a placeholder.
 */
export const durationParts = seconds => {
  if (seconds === null || seconds === undefined) return [];
  const total = Math.max(0, Math.round(seconds));
  if (total < MINUTE) return [{ unit: 'SECONDS', value: total }];

  const days = Math.floor(total / DAY);
  const hours = Math.floor((total % DAY) / HOUR);
  const minutes = Math.floor((total % HOUR) / MINUTE);

  if (days > 0) {
    return hours > 0
      ? [
          { unit: 'DAYS', value: days },
          { unit: 'HOURS', value: hours },
        ]
      : [{ unit: 'DAYS', value: days }];
  }
  if (hours > 0) {
    return minutes > 0
      ? [
          { unit: 'HOURS', value: hours },
          { unit: 'MINUTES', value: minutes },
        ]
      : [{ unit: 'HOURS', value: hours }];
  }
  return [{ unit: 'MINUTES', value: minutes }];
};

/**
 * Localized compact duration. `t` is the vue-i18n translate function; unit suffixes
 * live under KANBAN.REPORTS.DURATION so there are no bare strings.
 */
export const formatDuration = (seconds, t) => {
  const parts = durationParts(seconds);
  if (!parts.length) return t('KANBAN.REPORTS.DURATION.NONE');
  return parts
    .map(part =>
      t(`KANBAN.REPORTS.DURATION.${part.unit}`, { value: part.value })
    )
    .join(' ');
};

export const formatCurrencyValue = (
  value,
  currency,
  locale,
  { compact = false } = {}
) =>
  new Intl.NumberFormat(intlLocale(locale), {
    style: 'currency',
    currency: currency || 'USD',
    notation: compact ? 'compact' : 'standard',
    minimumFractionDigits: compact ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value || 0);

export const formatNumber = (value, locale) =>
  new Intl.NumberFormat(intlLocale(locale)).format(value || 0);

export const formatPercent = (ratio, locale) =>
  new Intl.NumberFormat(intlLocale(locale), {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(ratio || 0);

/**
 * X-axis label for a time-series bucket. Takes the bucket's local ISO date (yyyy-mm-dd)
 * provided by the backend and formats it in UTC, so the label always matches the
 * server-side bucket and stays identical across charts regardless of the viewer's browser
 * timezone. Formatting a date-only ISO string (parsed as UTC midnight) in UTC never shifts
 * the day, unlike formatting a raw epoch in browser-local time.
 */
export const formatPeriodLabel = (isoDate, groupBy, locale) => {
  const options =
    groupBy === 'month'
      ? { month: 'short', year: '2-digit', timeZone: 'UTC' }
      : { day: '2-digit', month: 'short', timeZone: 'UTC' };
  return new Intl.DateTimeFormat(intlLocale(locale), options).format(
    new Date(isoDate)
  );
};

/**
 * Period-over-period trend. Returns null when the previous value is missing or zero
 * (so the UI shows no misleading delta), otherwise a direction plus the absolute ratio.
 */
export const computeTrend = (current, previous) => {
  if (previous === null || previous === undefined || previous === 0)
    return null;
  const cur = current || 0;
  const change = (cur - previous) / Math.abs(previous);
  if (change === 0) return { direction: 'flat', ratio: 0 };
  return { direction: change > 0 ? 'up' : 'down', ratio: Math.abs(change) };
};
